/**
 * StyledTextField Utility Functions
 *
 * Helper functions for accessibility, styling, and field management.
 * All functions use theme tokens exclusively - NO hardcoded values.
 */

import type { Theme } from '@mui/material/styles';
import type {
  StyledTextFieldProps,
  AccessibilityProps,
  FieldStyleProps
} from './StyledTextField.types';

// ============================================================================
// ACCESSIBILITY UTILITIES
// ============================================================================

/**
 * Generate accessibility props for the text field
 */
export const getAccessibilityProps = (props: StyledTextFieldProps): AccessibilityProps => {
  const {
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
    required,
    error,
    name,
    helperText,
    errorMessage
  } = props;

  // Determine if field has error state
  const hasError = error || false;

  // Generate describedby reference for helper/error text
  const describedBy = ariaDescribedBy ||
    (helperText || errorMessage ? `${name || 'field'}-helper` : undefined);

  return {
    'aria-label': ariaLabel,
    'aria-describedby': describedBy,
    'aria-invalid': ariaInvalid !== undefined ? ariaInvalid : hasError,
    'aria-required': required,
  };
};

/**
 * Generate input props with accessibility attributes
 *
 * NOTE: `maxLength`/`minLength`/`pattern` are deliberately NOT included here.
 * This object is spread into the MUI `TextField`'s `slotProps.input` (the
 * legacy `InputProps` slot — props of the `OutlinedInput`/`InputBase`
 * WRAPPER), not `slotProps.htmlInput` (legacy `inputProps`, the actual
 * native `<input>`/`<textarea>` element). `InputBase` does not recognise
 * `maxLength`/`minLength`/`pattern` as its own top-level props, so passed
 * this way they silently land as unrecognised attributes on the wrapper
 * `<div>` instead of capping the real input — see `getHtmlInputProps` below,
 * which the component wires to `slotProps.htmlInput` instead.
 */
export const getInputProps = (props: StyledTextFieldProps, accessibilityProps: AccessibilityProps) => {
  const {
    autoComplete,
    autoFocus,
    spellCheck,
    readOnly,
    name
  } = props;

  return {
    ...accessibilityProps,
    autoComplete,
    autoFocus,
    spellCheck,
    readOnly,
    name,
  };
};

/**
 * Native-element-only attributes — must reach `slotProps.htmlInput` (see
 * `getInputProps` note above), never `slotProps.input`.
 */
export const getHtmlInputProps = (props: StyledTextFieldProps) => {
  const { maxLength, minLength, pattern } = props;
  return { maxLength, minLength, pattern };
};

// ============================================================================
// THEME UTILITIES
// ============================================================================

/**
 * Get field-specific styles based on state and theme
 */
export const getFieldStyles = (theme: Theme, styleProps: FieldStyleProps) => {
  const { hasError, hasRecentUpdate, updateSource } = styleProps;

  return {
    // Base field styling
    '& .MuiOutlinedInput-root': {
      // Error state
      ...(hasError && {
        '& fieldset': {
          borderColor: theme.palette.error.main,
        },
        '&:hover fieldset': {
          borderColor: theme.palette.error.main,
        },
        '&.Mui-focused fieldset': {
          borderColor: theme.palette.error.main,
          borderWidth: 2,
        },
      }),

      // Recent update styling
      ...(hasRecentUpdate && {
        '&:not(.Mui-error)': {
          '& fieldset': {
            borderWidth: 2,
            borderColor: getUpdateSourceColor(theme, updateSource),
          },
        },
      }),
    },

    // Label styling
    '& .MuiInputLabel-root': {
      color: hasError ? theme.palette.error.main : theme.palette.text.secondary,
      '&.Mui-focused': {
        color: hasError ? theme.palette.error.main : theme.palette.primary.main,
      },
    },

    // Helper text styling
    '& .MuiFormHelperText-root': {
      color: hasError ? theme.palette.error.main : theme.palette.text.secondary,
      marginLeft: 0,
    },
  };
};

/**
 * Get color for update source
 */
export const getUpdateSourceColor = (theme: Theme, source: string): string => {
  switch (source) {
    case 'firebase':
      return theme.palette.info.main;
    case 'system':
      return theme.palette.warning.main;
    case 'ai':
      return theme.palette.secondary.main;
    case 'user':
    default:
      return theme.palette.success.main;
  }
};

/**
 * Get responsive field width
 */
export const getFieldWidth = (fullWidth: boolean | undefined, _theme: Theme) => {
  if (fullWidth) return '100%';

  // MUI's `sx` parser expects breakpoint names when this object is used as the
  // value of `width`. Nesting full `@media (min-width:...)` keys under `width`
  // makes Emotion interpret them as kebab-case CSS properties and logs runtime
  // errors on every non-full-width field.
  return {
    xs: '100%',
    sm: '280px',
    md: '320px',
  };
};

// ============================================================================
// VALIDATION UTILITIES
// ============================================================================

/**
 * Check if field should validate on specific trigger
 */
export const shouldValidate = (
  trigger: 'change' | 'blur' | 'submit',
  validateOnChange?: boolean,
  validateOnBlur?: boolean
): boolean => {
  switch (trigger) {
    case 'change':
      return validateOnChange || false;
    case 'blur':
      return validateOnBlur || false;
    case 'submit':
      return true; // Always validate on submit
    default:
      return false;
  }
};

/**
 * Get validation timing description for debugging
 */
export const getValidationTiming = (props: StyledTextFieldProps): string => {
  const { validateOnChange, validateOnBlur } = props;

  if (validateOnChange && validateOnBlur) return 'change + blur';
  if (validateOnChange) return 'change';
  if (validateOnBlur) return 'blur';
  return 'submit only';
};

// ============================================================================
// FIELD MANAGEMENT UTILITIES
// ============================================================================

/**
 * Generate unique field ID
 */
export const generateFieldId = (name?: string, label?: string): string => {
  if (name) return `field-${name}`;
  if (label) return `field-${label.toLowerCase().replace(/\s+/g, '-')}`;
  return `field-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Get helper text ID for aria-describedby
 */
export const getHelperTextId = (fieldId: string, hasError: boolean): string => {
  return `${fieldId}-${hasError ? 'error' : 'helper'}`;
};

/**
 * Check if field has any validation rules
 */
export const hasValidationRules = (props: StyledTextFieldProps): boolean => {
  const {
    required,
    rules,
    validateEmail,
    validateUrl,
    customValidation,
    type
  } = props;

  return !!(
    required ||
    (rules && rules.length > 0) ||
    validateEmail ||
    validateUrl ||
    customValidation ||
    type === 'email' ||
    type === 'url' ||
    type === 'tel'
  );
};

/**
 * Get field type-specific attributes
 */
export const getTypeAttributes = (type: string = 'text') => {
  const attributes: Record<string, any> = {};

  switch (type) {
    case 'email':
      attributes.inputMode = 'email';
      attributes.autoCapitalize = 'none';
      attributes.autoCorrect = 'off';
      break;
    case 'url':
      attributes.inputMode = 'url';
      attributes.autoCapitalize = 'none';
      attributes.autoCorrect = 'off';
      break;
    case 'tel':
      attributes.inputMode = 'tel';
      attributes.autoComplete = 'tel';
      break;
    case 'search':
      attributes.autoCapitalize = 'none';
      attributes.autoCorrect = 'off';
      break;
    case 'password':
      attributes.autoComplete = 'current-password';
      break;
    default:
      break;
  }

  return attributes;
};

// ============================================================================
// PERFORMANCE UTILITIES
// ============================================================================

/**
 * Shallow comparison for React.memo optimization
 */
export const shallowCompareProps = (
  prevProps: StyledTextFieldProps,
  nextProps: StyledTextFieldProps
): boolean => {
  // Critical props that should trigger re-render
  const criticalProps = [
    'value', 'error', 'errorMessage', 'disabled', 'readOnly', 'required',
    'label', 'placeholder', 'helperText', 'variant', 'size', 'color',
    'fieldPath', 'showUpdateIndicator', 'trackFieldUpdates'
  ] as const;

  for (const prop of criticalProps) {
    if (prevProps[prop] !== nextProps[prop]) {
      return false; // Props changed, re-render needed
    }
  }

  // Check validation rules array
  if (JSON.stringify(prevProps.rules) !== JSON.stringify(nextProps.rules)) {
    return false;
  }

  // Check sx prop
  if (JSON.stringify(prevProps.sx) !== JSON.stringify(nextProps.sx)) {
    return false;
  }

  return true; // No significant changes, skip re-render
};

/**
 * Debounce function for validation
 */
export const createDebouncer = (delay: number = 300) => {
  let timeoutId: NodeJS.Timeout;

  return (callback: () => void) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(callback, delay);
  };
};
